/**
 * Walidacja typow plikow - mirror kontraktu PHP:
 * `IVALIDATOR::getFileTypeValidation(IVALIDATOR_FILETYPE_*)` z
 * `web/IMPERIAL/IVALIDATOR.php`.
 *
 * Dlaczego osobny modul (a nie inline w komponencie): HTML atrybut `accept`
 * w `<input type="file">` to TYLKO hint dla przegladarki - drag-drop oraz
 * dialog "All files" potrafia go obejsc. Faktyczna walidacja musi sprawdzic
 * MIME i rozszerzenie w JS przed wyslaniem multipart'u (a backend i tak
 * powinien walidowac drugi raz - frontend-only validation to UX, nie security).
 *
 * Listy dozwolonych typow trzymamy w jednym miejscu zeby przyszle pluginy
 * (CFiles, CAvatar, etc.) korzystaly z tego samego kontraktu i automatycznie
 * podazaly za zmianami w IVALIDATOR.php.
 */

export type FileTypeSpec = {
  /** Dozwolone MIME types (lowercase). */
  mime: ReadonlySet<string>;
  /** Dozwolone rozszerzenia plikow (lowercase, bez kropki). */
  ext: ReadonlySet<string>;
  /** Wartosc do atrybutu `accept` w `<input type="file">`. */
  accept: string;
};

/** IVALIDATOR_FILETYPE_IMAGE - jpeg/jpg/png/gif/webp. */
export const FILETYPE_IMAGE: FileTypeSpec = {
  mime: new Set(["image/jpeg", "image/pjpeg", "image/png", "image/gif", "image/webp"]),
  ext: new Set(["jpeg", "jpg", "png", "gif", "webp"]),
  accept: "image/jpeg,image/png,image/gif,image/webp",
};

/** IVALIDATOR_FILETYPE_IMAGE_SVG - IMAGE + SVG (LOGO upload). */
export const FILETYPE_IMAGE_SVG: FileTypeSpec = {
  mime: new Set([
    "image/jpeg",
    "image/pjpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/svg+xml",
  ]),
  ext: new Set(["jpeg", "jpg", "png", "gif", "webp", "svg"]),
  accept: "image/jpeg,image/png,image/gif,image/webp,image/svg+xml",
};

/** Wynik walidacji listy plikow - rozdzielone accepted / rejected. */
export type FileValidationResult = {
  accepted: File[];
  rejected: Array<{ file: File; reason: string }>;
};

/**
 * Rozdziela liste plikow na pasujace do spec'u i odrzucone (z powodem).
 * MIME i rozszerzenie MUSZA pasowac jednoczesnie (defense-in-depth - sam
 * MIME jest spoofowalny w naglowku multipart, samo rozszerzenie tez).
 */
export function validateFiles(files: FileList | File[], spec: FileTypeSpec): FileValidationResult {
  const accepted: File[] = [];
  const rejected: Array<{ file: File; reason: string }> = [];

  for (const file of Array.from(files)) {
    const mime = file.type.toLowerCase();
    const ext = file.name.includes(".")
      ? file.name.slice(file.name.lastIndexOf(".") + 1).toLowerCase()
      : "";

    if (!spec.mime.has(mime) || !spec.ext.has(ext)) {
      rejected.push({
        file,
        reason: `${file.name} - niedozwolony typ pliku (dozwolone: ${[...spec.ext].join(", ")}).`,
      });
      continue;
    }

    accepted.push(file);
  }

  return { accepted, rejected };
}
